"use client";

// ── "save it to your innernet" — the landing of a handed skill ──────────────
// A skill page's gateway (and a shared skill's) sends a signed-in visitor to
// `/dashboard?add=skill:<name>` or `?add=share:<token>` (`&v=` for a specific
// kept state). This mounts over the shell, asks the ONE thing that has to be
// asked — which of your folders it lands in — and saves the document exactly
// as it was kept. Then it opens the page. THE sheet, small (law_sheet_rises):
// one line above the foot saying what will happen, the pill bottom-right, "not
// now" quiet on the left.

import { useCallback, useEffect, useMemo, useState } from "react";
import EtherModal from "../ether/EtherModal";
import { PillButton } from "../ui";
import { parseSkillDoc } from "../../lib/skills/manifest";
import type { DashProject } from "../../app/dashboard/DashboardClient";
import DoorMark from "../brand/DoorMark";

interface Handed {
  source: string; // skill:<name> | share:<token>
  v: string | null;
  label: string;
}

function readHanded(): Handed | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const add = q.get("add");
  if (!add || !/^(skill|share):[A-Za-z0-9_.-]+$/.test(add)) return null;
  const v = q.get("v");
  const label = add.startsWith("skill:") ? add.slice(6).replace(/-/g, " ") : "a shared skill";
  return { source: add, v: v && /^[A-Za-z0-9_.-]+$/.test(v) ? v : null, label };
}

export default function SaveHandedSkill({ projects }: { projects: DashProject[] }) {
  const [handed, setHanded] = useState<Handed | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // what the sheet says while it sinks (closing clears `handed` the same instant)
  const [last, setLast] = useState<Handed | null>(null);
  if (handed && handed !== last) setLast(handed);

  useEffect(() => {
    const h = readHanded();
    if (!h) return;
    setHanded(h);
    // a kept skill's TITLE, not its slug: read it off our own raw route (the
    // manifest is client-safe to parse). The slug stays as the fallback.
    if (!h.source.startsWith("skill:")) return;
    const ctl = new AbortController();
    fetch(`/skills/${h.source.slice(6)}/raw${h.v ? `?v=${encodeURIComponent(h.v)}` : ""}`, { signal: ctl.signal })
      .then((r) => (r.ok ? r.text() : null))
      .then((raw) => {
        const title = raw ? parseSkillDoc(raw)?.manifest.title : null;
        if (title) setHanded((cur) => (cur && cur.source === h.source ? { ...cur, label: title } : cur));
      })
      .catch(() => { /* the slug reads fine */ });
    return () => ctl.abort();
  }, []);

  // top-level folders, most recently touched first — the likely home.
  const folders = useMemo(
    () => projects.filter((p) => !p.parentId).sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? "")),
    [projects],
  );
  useEffect(() => {
    if (!projectId && folders[0]) setProjectId(folders[0].id);
  }, [folders, projectId]);

  const close = useCallback(() => {
    setHanded(null);
    // leave the address clean so a reload doesn't ask again.
    const u = new URL(window.location.href);
    u.searchParams.delete("add");
    u.searchParams.delete("v");
    window.history.replaceState(null, "", u.pathname + (u.search || ""));
  }, []);

  const save = useCallback(async () => {
    if (!handed || !projectId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("/api/community/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: handed.source, v: handed.v ?? undefined, projectId }),
      });
      const json = (await res.json()) as { ok?: boolean; path?: string; error?: string };
      if (!json.ok || !json.path) {
        setErr(json.error ?? "that didn't take. try again.");
        setBusy(false);
        return;
      }
      close();
      // the page lives inside its folder's skills — open the folder (a full
      // route; the shell descends from there and the new page is in the list).
      window.location.assign(`/projects/${projectId}`);
    } catch {
      setErr("that didn't take. try again.");
      setBusy(false);
    }
  }, [handed, projectId, busy, close]);

  const view = handed ?? last;
  if (!view) return null;
  const chosen = folders.find((f) => f.id === projectId) ?? null;

  return (
    <EtherModal
      open={!!handed}
      onClose={close}
      labelledBy="shs-title"
      size="sm"
      height="fit"
      header={
        <>
          <h2 id="shs-title" className="eth-title">save {view.label} to your innernet</h2>
          <p className="eth-meta">
            choose where it lands. it arrives exactly as it was kept
            {view.v ? ", this specific version" : ""}, and gathers into that folder&rsquo;s skills on its own.
          </p>
        </>
      }
      footStart={
        <button type="button" className="quiet-link" onClick={close} disabled={busy}>not now</button>
      }
      footer={
        <PillButton
          className="btn-pill--solid"
          onClick={() => void save()}
          disabled={busy || !projectId}
          icon={<DoorMark />}
        >
          {busy ? "saving…" : "save it here"}
        </PillButton>
      }
    >
      {folders.length === 0 ? (
        <p className="rt-empty">you don&rsquo;t have a folder yet. make one first, then come back to this link.</p>
      ) : (
        <div className="list rt-pick" role="radiogroup" aria-label="which folder">
          {folders.slice(0, 12).map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={p.id === projectId}
              className="list-row"
              data-on={p.id === projectId || undefined}
              onClick={() => setProjectId(p.id)}
              style={{ textAlign: "left", background: "none", border: 0, borderBottom: "1px solid var(--hair-soft)", font: "inherit", cursor: "pointer", width: "100%" }}
            >
              <span className="list-name" style={{ color: p.id === projectId ? "var(--ink)" : "var(--ink-2)" }}>
                {p.id === projectId && <span className="ink-dot" aria-hidden style={{ marginRight: "0.5rem" }} />}
                {p.name}
              </span>
              <span className="list-meta">{p.dimCount} {p.dimCount === 1 ? "page" : "pages"}</span>
            </button>
          ))}
        </div>
      )}
      <p className="rt-says" data-warn={err ? "" : undefined}>
        {err ?? (chosen ? `one page will be added to ${chosen.name}. nothing else changes.` : "choose a folder.")}
      </p>
    </EtherModal>
  );
}
